import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import supabase from "./supabaseClient";
import { apiCall } from "./authFetch";
import authFetch from "./authFetch";

/* ── camelCase ↔ snake_case ─────────────────────────────── */
const toSnake = (s) => s.replace(/[A-Z]/g, (c) => "_" + c.toLowerCase());
const toCamel = (s) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
function keysToSnake(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) out[toSnake(k)] = v;
  return out;
}
function keysToCamel(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) out[toCamel(k)] = v;
  return out;
}

/* ── Table ↔ State key mapping ──────────────────────────── */
const TABLE_TO_STATE = {
  income_records:     "income",
  recurring_income:   "recurringIncome",
  income_categories:  "incomeCats",
  expense_records:    "expenses",
  expense_categories: "expenseCats",
  recurring_expenses: "recurringExpenses",
  holdings:           "holdings",
  investment_liabilities: "investmentLiabilities",
  balance_items:      "balanceItems",
  projects:           "projects",
  staff:              "staff",
  leave_requests:     "leaveRequests",
  loan_requests:      "loanRequests",
  workspace_users:    "users",
  locations:          "locations",
  vehicles:           "vehicles",
  vehicle_maintenance_records: "vehicleMaintRecords",
  vehicle_mileage_log: "vehicleMileageLog",
  groceries:          "groceries",
  maintenance:        "maintenance",
  maintenance_schedules: "maintenanceSchedules",
  suppliers:          "suppliers",
  transport_requests: "transportRequests",
  cal_accounts:       "calAccounts",
  meetings:           "meetings",
  meals:                    "meals",
  meal_preferences:         "mealPreferences",
  menu_cycles:              "menuCycles",
  menu_generated_options:   "menuGeneratedOptions",
  menu_votes:               "menuVotes",
  notifications:            "notifications",
  tasks:                    "tasks",
  task_engine_config:       "taskConfigs",
  task_activity_log:        "taskActivityLog",
  task_escalation_log:      "taskEscalationLog",
  task_taxonomy:            "taskTaxonomy",
};

const STATE_TO_TABLE = {};
for (const [t, s] of Object.entries(TABLE_TO_STATE)) STATE_TO_TABLE[s] = t;

const ALL_TABLES = Object.keys(TABLE_TO_STATE);

const CONFIG_KEYS = [
  "lang", "currency", "appName", "tagline", "locFilter", "dashboardConfig",
  "branding", "modules", "navOrder", "moduleSubtitles", "apiKeys",
  "notifPrefs", "hats", "staffRoles", "staffAuthorities",
  "mealCategories", "maintenanceCategories", "investmentCategories", "docTags",
  "platformModuleNames", "wsAiDisabled", "wsAiAllDisabled",
  "menuDietaryRequirements", "menuPermissions", "menuPreferences", "menuDeadlineOverride",
  "menuVotingModeOverride", "menuOptionsPerCourseOverride", "menuCoursesOverride",
  "householdData",
  "investmentAlertSettings",
  "incomePermissions",
  "incomeAlertSettings",
  "expensePermissions",
  "expenseAlertSettings",
  "taskVisibilityOverrides",
  "staffPermissions",
  "staffViewPermissions",
  "facilityPermissions",
  "investmentPermissions",
  "facilityConfig",
  "groceryConfig",
  "groceryCategories",
  "fleetServiceCategories",
  "supplierPermissions",
  "supplierConfig",
  "fleetConfig",
  "fleetPermissions",
  "transportConfig",
  "transportPermissions",
  "smartHomeConnections",
  "smartHomePermissions",
  "smartHomeEntityLabels",
  "smartHomeNotifPrefs",
  "financialTargets",
  "onboardingState",
];

/* apiCall imported from ./authFetch — single centralized API client */

/* ── Main Hook ──────────────────────────────────────────── */
export default function useRealtimeSync(wsKey, SEED) {
  /* Entity data: always starts empty — DB is the only truth */
  const [entities, setEntities] = useState(() => {
    const init = {};
    for (const stateKey of Object.values(TABLE_TO_STATE)) {
      init[stateKey] = SEED[stateKey] || [];
    }
    return init;
  });

  /* Config data: localStorage cache is fine for config (no conflicts expected) */
  const [config, setConfig] = useState(() => {
    try {
      const raw = localStorage.getItem(`cmd_cfg_${wsKey}`);
      if (raw) return JSON.parse(raw);
    } catch {}
    const c = {};
    for (const k of CONFIG_KEYS) c[k] = SEED[k];
    return c;
  });

  const [loading, setLoading] = useState(true);
  const [syncError, setSyncError] = useState(null);
  const dbReady = useRef(false);
  const fetchingRef = useRef(false);
  /* In-memory pending writes counter — blocks refetchAll while writes are in-flight.
     NOT persisted to localStorage (so stale counts never block future sessions). */
  const pendingWritesRef = useRef(0);
  /* Optimistic items: tracks {stateKey → Set<id>} for items added locally but not
     yet confirmed by the DB. refetchAll preserves these instead of dropping them. */
  const optimisticRef = useRef({});

  /* Config persistence (config-only — no entity localStorage) */
  useEffect(() => {
    try { localStorage.setItem(`cmd_cfg_${wsKey}`, JSON.stringify(config)); } catch {}
  }, [config, wsKey]);

  /* ── 1. Initial hydrate from DB ───────────────────────── */
  useEffect(() => {
    let cancelled = false;

    async function hydrate() {
      try {
        const migrateRes = await apiCall("POST", "/api/migrate", { ws_key: wsKey }).catch((e) => {
          console.warn("[hydrate] migrate failed (non-fatal):", e.message);
          return null;
        });
        if (migrateRes?.error) console.warn("[migrate]", migrateRes.error);

        const tableResults = await Promise.allSettled(
          ALL_TABLES.map(async (table) => {
            const res = await apiCall("GET", `/api/crud?table=${table}&ws_key=${encodeURIComponent(wsKey)}`);
            return { table, rows: res.rows || [] };
          })
        );

        if (cancelled) return;

        const newEntities = {};
        const failedTables = [];
        const statusCounts = {};
        let totalRowsReturned = 0;
        for (const result of tableResults) {
          if (result.status === "fulfilled") {
            const { table, rows } = result.value;
            newEntities[TABLE_TO_STATE[table]] = rows;
            totalRowsReturned += rows.length;
          } else {
            /* Track failures so we can report them and retry */
            const reason = result.reason;
            const status = reason?.status || "?";
            const message = reason?.message || String(reason) || "unknown";
            failedTables.push({ status, message });
            statusCounts[status] = (statusCounts[status] || 0) + 1;
          }
        }

        /* Loud diagnostic: if EVERY fetch succeeded but returned zero rows,
           something is wrong (auth mismatch, wrong ws_key, stale token, etc).
           Log it prominently so the user can see what's happening without
           needing deep DevTools spelunking. */
        if (failedTables.length === 0 && totalRowsReturned === 0) {
          console.error(
            "[hydrate] ⚠ All %d tables returned zero rows for ws_key=%s. " +
            "This usually means the JWT's wsKey claim does not match the workspace " +
            "you're viewing, or all rows have deleted_at set. Check the Network tab " +
            "response for /api/crud?table=expense_records.",
            ALL_TABLES.length, wsKey
          );
          setSyncError(`Sync returned 0 rows for all ${ALL_TABLES.length} tables. Token/workspace mismatch?`);
        } else if (failedTables.length > 0) {
          /* Aggregate by status code so the error is actually informative */
          const statusSummary = Object.entries(statusCounts)
            .map(([s, c]) => `${c}×${s}`)
            .join(", ");
          const firstMsg = failedTables[0].message;
          console.error("[hydrate] %d table fetches rejected — status breakdown: %s", failedTables.length, statusSummary, failedTables);
          setSyncError(`${failedTables.length} fetches failed (${statusSummary}). First error: ${firstMsg}`);
        }

        /* Smart merge: preserve optimistic items that haven't been confirmed by DB yet.
           SAFETY: never overwrite a populated local entity with an empty DB response. */
        setEntities((prev) => {
          const merged = { ...prev };
          for (const [stateKey, rows] of Object.entries(newEntities)) {
            const optimisticIds = optimisticRef.current[stateKey];
            if (optimisticIds && optimisticIds.size > 0) {
              const dbIds = new Set(rows.map((r) => r.id));
              const keptFromLocal = (prev[stateKey] || []).filter((r) => optimisticIds.has(r.id) && !dbIds.has(r.id));
              const keptIds = new Set(keptFromLocal.map((r) => r.id));
              const dbFiltered = rows.filter((r) => !keptIds.has(r.id));
              merged[stateKey] = [...keptFromLocal, ...dbFiltered];
            } else if (rows.length === 0 && (prev[stateKey] || []).length > 0) {
              /* Safety: DB says empty but we have local data. Preserve local. */
              merged[stateKey] = prev[stateKey];
            } else {
              merged[stateKey] = rows;
            }
          }
          return merged;
        });

        const cfgRes = await apiCall("GET", `/api/config?ws_key=${encodeURIComponent(wsKey)}`).catch((e) => {
          console.warn("[hydrate] config fetch failed:", e.message);
          return null;
        });
        if (cfgRes?.config && !cancelled) {
          setConfig((prev) => ({ ...prev, ...cfgRes.config }));
        }

        dbReady.current = true;
        setLoading(false);
      } catch (err) {
        console.error("[useRealtimeSync] hydrate error:", err);
        setSyncError(err.message);
        dbReady.current = true;
        setLoading(false);
      }
    }

    hydrate();
    return () => { cancelled = true; };
  }, [wsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── 2. Supabase Realtime subscriptions ───────────────── */
  useEffect(() => {
    if (!supabase) return;

    const channels = [];

    for (const table of ALL_TABLES) {
      const stateKey = TABLE_TO_STATE[table];
      const ch = supabase
        .channel(`rt_${wsKey}_${table}`)
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table, filter: `ws_key=eq.${wsKey}` },
          (payload) => {
            const { eventType, new: newRow, old: oldRow } = payload;
            const row = keysToCamel(newRow || oldRow);

            setEntities((prev) => {
              const arr = prev[stateKey] || [];
              if (eventType === "INSERT") {
                /* Clear from optimistic set — DB confirmed this item */
                if (optimisticRef.current[stateKey]) optimisticRef.current[stateKey].delete(row.id);
                /* If already present (optimistic add), replace with DB-confirmed version */
                if (arr.some((r) => r.id === row.id)) {
                  return { ...prev, [stateKey]: arr.map((r) => r.id === row.id ? row : r) };
                }
                return { ...prev, [stateKey]: [row, ...arr] };
              }
              if (eventType === "UPDATE") {
                if (row.deletedAt) {
                  return { ...prev, [stateKey]: arr.filter((r) => r.id !== row.id) };
                }
                /* For optimistic items, only accept realtime if it confirms our write
                   (i.e., the incoming row's updatedAt is newer than local) */
                if (optimisticRef.current[stateKey]?.has(row.id)) {
                  const local = arr.find((r) => r.id === row.id);
                  const localTs = local?.updatedAt ? new Date(local.updatedAt).getTime() : 0;
                  const remoteTs = row.updatedAt ? new Date(row.updatedAt).getTime() : 0;
                  if (remoteTs > localTs) {
                    /* DB confirmed our write — accept and release optimistic hold */
                    optimisticRef.current[stateKey].delete(row.id);
                    return { ...prev, [stateKey]: arr.map((r) => r.id === row.id ? row : r) };
                  }
                  return prev; /* Stale event — skip */
                }
                const exists = arr.some((r) => r.id === row.id);
                if (!exists) return { ...prev, [stateKey]: [row, ...arr] };
                /* Last-write-wins: only update if incoming row is newer */
                return {
                  ...prev,
                  [stateKey]: arr.map((r) => {
                    if (r.id !== row.id) return r;
                    const localTs = r.updatedAt ? new Date(r.updatedAt).getTime() : 0;
                    const remoteTs = row.updatedAt ? new Date(row.updatedAt).getTime() : 0;
                    return remoteTs >= localTs ? { ...r, ...row } : r;
                  }),
                };
              }
              if (eventType === "DELETE") {
                return { ...prev, [stateKey]: arr.filter((r) => r.id !== (row?.id || oldRow?.id)) };
              }
              return prev;
            });
          }
        )
        .subscribe();
      channels.push(ch);
    }

    /* Subscribe to workspace_config changes */
    const cfgCh = supabase
      .channel(`rt_${wsKey}_config`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "workspace_config", filter: `ws_key=eq.${wsKey}` },
        (payload) => {
          const row = payload.new;
          if (row?.key) {
            setConfig((prev) => ({ ...prev, [row.key]: row.value }));
          }
        }
      )
      .subscribe();
    channels.push(cfgCh);

    return () => {
      for (const ch of channels) supabase.removeChannel(ch);
    };
  }, [wsKey]);

  /* ── 3. Polling fallback — blocked while writes are in-flight ── */
  const refetchAll = useCallback(async () => {
    if (!dbReady.current) return;
    if (fetchingRef.current) return; /* prevent overlapping fetches */
    if (pendingWritesRef.current > 0) return; /* don't overwrite optimistic state */
    fetchingRef.current = true;
    try {
      const tableResults = await Promise.allSettled(
        ALL_TABLES.map(async (table) => {
          const res = await apiCall("GET", `/api/crud?table=${table}&ws_key=${encodeURIComponent(wsKey)}`);
          return { table, rows: res.rows || [] };
        })
      );
      const dbData = {};
      for (const result of tableResults) {
        if (result.status === "fulfilled") {
          const { table, rows } = result.value;
          dbData[TABLE_TO_STATE[table]] = rows;
        }
      }
      /* Smart merge: preserve optimistic items (both new adds and in-flight updates).
         SAFETY: never overwrite a populated local entity with an empty DB response.
         Additional safety: if the refetch returned ZERO rows for every table but
         local state has data, treat it as a bad fetch (auth mismatch, wrong
         ws_key, network hiccup) and skip the merge entirely. */
      const totalDbRows = Object.values(dbData).reduce((sum, rows) => sum + (rows?.length || 0), 0);
      setEntities((prev) => {
        const localHasAny = Object.values(prev).some((rows) => (rows || []).length > 0);
        if (totalDbRows === 0 && localHasAny) {
          console.warn("[refetchAll] ⚠ SKIPPING MERGE: all tables returned 0 rows but local state has data. Likely a transient auth/fetch issue. Preserving local state.");
          return prev;
        }
        const merged = { ...prev };
        for (const [stateKey, rows] of Object.entries(dbData)) {
          const optimisticIds = optimisticRef.current[stateKey];
          if (optimisticIds && optimisticIds.size > 0) {
            const dbMap = new Map(rows.map((r) => [r.id, r]));
            const localArr = prev[stateKey] || [];
            const result = [];
            const handledIds = new Set();

            /* For each optimistic ID, decide: keep local or accept DB version */
            for (const localRow of localArr) {
              if (optimisticIds.has(localRow.id)) {
                const dbRow = dbMap.get(localRow.id);
                if (!dbRow) {
                  /* Not in DB yet (new add) — keep local */
                  result.push(localRow);
                } else {
                  /* In DB — only accept DB version if its updatedAt is >= our
                     optimistic write (meaning DB confirmed our change) */
                  const localTs = localRow.updatedAt ? new Date(localRow.updatedAt).getTime() : 0;
                  const dbTs = dbRow.updatedAt ? new Date(dbRow.updatedAt).getTime() : 0;
                  if (dbTs > localTs) {
                    result.push(dbRow);
                    optimisticIds.delete(localRow.id); /* DB confirmed — release */
                  } else {
                    result.push(localRow); /* DB row is stale — keep local */
                  }
                }
                handledIds.add(localRow.id);
              }
            }
            /* Add all non-optimistic rows from DB */
            for (const dbRow of rows) {
              if (!handledIds.has(dbRow.id)) result.push(dbRow);
            }
            merged[stateKey] = result;
          } else if (rows.length === 0 && (prev[stateKey] || []).length > 0) {
            /* Safety: DB says empty for this table but local has data. Preserve local. */
            merged[stateKey] = prev[stateKey];
          } else {
            merged[stateKey] = rows;
          }
        }
        return merged;
      });

      const cfgRes = await apiCall("GET", `/api/config?ws_key=${encodeURIComponent(wsKey)}`);
      if (cfgRes.config) setConfig((prev) => ({ ...prev, ...cfgRes.config }));
    } catch (err) {
      console.warn("[useRealtimeSync] refetch error:", err.message);
    } finally {
      fetchingRef.current = false;
    }
  }, [wsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Refetch when tab becomes visible (cross-device sync recovery) */
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") refetchAll(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refetchAll]);

  /* Periodic poll every 8 seconds as realtime fallback */
  useEffect(() => {
    const id = setInterval(refetchAll, 8_000);
    return () => clearInterval(id);
  }, [refetchAll]);

  /* ── 4. CRUD helpers — optimistic + rollback on failure ── */
  const addRecord = useCallback(async (stateKey, row) => {
    const table = STATE_TO_TABLE[stateKey];
    if (!table) { console.error("[addRecord] unknown stateKey:", stateKey); return; }

    /* Track this item as optimistic so refetchAll preserves it */
    if (!optimisticRef.current[stateKey]) optimisticRef.current[stateKey] = new Set();
    optimisticRef.current[stateKey].add(row.id);

    /* Optimistic add + block refetchAll during API call AND for 5s after */
    setEntities((prev) => ({ ...prev, [stateKey]: [row, ...(prev[stateKey] || [])] }));
    pendingWritesRef.current += 1;

    try {
      await apiCall("POST", `/api/crud?table=${table}`, { table, ws_key: wsKey, row });
      /* Keep pendingWrites elevated for 5s after success to block refetchAll
         until realtime has had time to confirm the INSERT */
      setTimeout(() => {
        pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
      }, 5000);
      /* Clear from optimistic set after 15s as absolute safety net */
      setTimeout(() => { optimisticRef.current[stateKey]?.delete(row.id); }, 15000);
    } catch (err) {
      console.error(`[addRecord] ${stateKey} failed:`, err.message);
      /* Remove from optimistic set and roll back local state */
      optimisticRef.current[stateKey]?.delete(row.id);
      setEntities((prev) => ({
        ...prev,
        [stateKey]: (prev[stateKey] || []).filter((r) => r.id !== row.id),
      }));
      pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
    }
  }, [wsKey]);

  const updateRecord = useCallback(async (stateKey, id, patch, opts = {}) => {
    const table = STATE_TO_TABLE[stateKey];
    if (!table) return;

    /* Track this item as optimistic so refetchAll & realtime preserve local version */
    if (!optimisticRef.current[stateKey]) optimisticRef.current[stateKey] = new Set();
    optimisticRef.current[stateKey].add(id);

    /* Snapshot for rollback */
    let snapshot;

    /* Optimistic update + block refetchAll during API call */
    const optimisticPatch = { ...patch, updatedAt: new Date().toISOString() };
    setEntities((prev) => {
      snapshot = (prev[stateKey] || []).find((r) => r.id === id);
      return {
        ...prev,
        [stateKey]: (prev[stateKey] || []).map((r) => r.id === id ? { ...r, ...optimisticPatch } : r),
      };
    });
    pendingWritesRef.current += 1;

    try {
      /* For staff-related tables, send expectedUpdatedAt for optimistic locking
         Only when explicitly requested via opts.lockCheck (e.g., edit forms where
         user is modifying data loaded earlier). Skip for quick status toggles. */
      const putBody = { table, ws_key: wsKey, id, patch };
      if (opts.lockCheck && snapshot?.updatedAt) {
        putBody.expectedUpdatedAt = snapshot.updatedAt;
      }
      await apiCall("PUT", `/api/crud?table=${table}`, putBody);
      /* On success: keep pendingWrites elevated for 8s (> poll interval)
         to guarantee at least one full refetchAll cycle is blocked */
      setTimeout(() => {
        pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
      }, 8500);
      /* Clear optimistic protection only after realtime has had ample time
         to deliver the confirmed row (30s safety net) */
      setTimeout(() => { optimisticRef.current[stateKey]?.delete(id); }, 30000);
    } catch (err) {
      console.error("[updateRecord] failed — rolling back:", err.message);
      if (snapshot !== undefined) {
        setEntities((prev) => ({
          ...prev,
          [stateKey]: (prev[stateKey] || []).map((r) => r.id === id ? snapshot : r),
        }));
      }
      optimisticRef.current[stateKey]?.delete(id);
      pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
      throw err;
    }
  }, [wsKey]);

  const deleteRecord = useCallback(async (stateKey, id) => {
    const table = STATE_TO_TABLE[stateKey];
    if (!table) return;

    /* Snapshot for rollback */
    let snapshot;

    /* Optimistic remove + block refetchAll during API call */
    setEntities((prev) => {
      snapshot = (prev[stateKey] || []).find((r) => r.id === id);
      return {
        ...prev,
        [stateKey]: (prev[stateKey] || []).filter((r) => r.id !== id),
      };
    });
    pendingWritesRef.current += 1;

    try {
      await apiCall("DELETE", `/api/crud?table=${table}&ws_key=${encodeURIComponent(wsKey)}&id=${encodeURIComponent(id)}`);
    } catch (err) {
      console.error("[deleteRecord] failed — rolling back:", err.message);
      if (snapshot !== undefined) {
        setEntities((prev) => ({
          ...prev,
          [stateKey]: [snapshot, ...(prev[stateKey] || [])],
        }));
      }
      throw err;
    } finally {
      pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
    }
  }, [wsKey]);

  const setConfigKey = useCallback(async (key, value) => {
    /* Optimistic */
    setConfig((prev) => ({ ...prev, [key]: value }));

    try {
      await apiCall("POST", "/api/config", { ws_key: wsKey, key, value });
    } catch (err) {
      console.error("[setConfig] error:", err);
      /* Config failures are non-critical; realtime will eventually correct */
    }
  }, [wsKey]);

  /* ── 5. Combined dispatch (backward-compatible) ───────── */
  const combinedDispatch = useCallback((action) => {
    const t = action.type;

    /* ── Income ────────────────────────────────────────── */
    if (t === "ADD_INCOME") return addRecord("income", action.income);
    if (t === "UPDATE_INCOME") return updateRecord("income", action.id, action.patch);
    if (t === "REMOVE_INCOME") return deleteRecord("income", action.id);

    if (t === "ADD_RECURRING_INCOME") return addRecord("recurringIncome", action.rec);
    if (t === "TOGGLE_REC_INCOME") {
      const rec = (entities.recurringIncome || []).find((r) => r.id === action.id);
      return updateRecord("recurringIncome", action.id, { active: !rec?.active });
    }
    if (t === "UPDATE_RECURRING_INCOME") return updateRecord("recurringIncome", action.id, action.patch);
    if (t === "REMOVE_RECURRING_INCOME") return deleteRecord("recurringIncome", action.id);

    if (t === "ADD_INCOME_CAT") return addRecord("incomeCats", { id: `ic${Date.now()}`, name: action.name, nameAr: action.nameAr || "", parent: action.parent || null });
    if (t === "REMOVE_INCOME_CAT") return deleteRecord("incomeCats", action.id);
    if (t === "RENAME_INCOME_CAT") {
      const patch = {};
      if (action.name !== undefined) patch.name = action.name;
      if (action.nameAr !== undefined) patch.nameAr = action.nameAr;
      return updateRecord("incomeCats", action.id, patch);
    }

    /* ── Expenses ──────────────────────────────────────── */
    if (t === "ADD_EXPENSE") return addRecord("expenses", action.expense);
    if (t === "UPDATE_EXPENSE") return updateRecord("expenses", action.id, action.patch);
    if (t === "REMOVE_EXPENSE") return deleteRecord("expenses", action.id);
    if (t === "APPROVE_EXP") return updateRecord("expenses", action.id, { approved: true });
    if (t === "REJECT_EXP") return updateRecord("expenses", action.id, { approved: false, rejected: true, reject_reason: action.reason || "" });

    if (t === "ADD_RECURRING") return addRecord("recurringExpenses", action.rec);
    if (t === "TOGGLE_REC") {
      const rec = (entities.recurringExpenses || []).find((r) => r.id === action.id);
      return updateRecord("recurringExpenses", action.id, { active: !rec?.active });
    }
    if (t === "UPDATE_RECURRING") return updateRecord("recurringExpenses", action.id, action.patch);
    if (t === "REMOVE_RECURRING") return deleteRecord("recurringExpenses", action.id);
    if (t === "UPDATE_RECURRING_EXP") return updateRecord("recurringExpenses", action.id, action.patch);
    if (t === "REMOVE_RECURRING_EXP") return deleteRecord("recurringExpenses", action.id);

    if (t === "ADD_CAT") return addRecord("expenseCats", action.cat);
    if (t === "REMOVE_CAT") return deleteRecord("expenseCats", action.id);
    if (t === "RENAME_CAT") {
      const patch = {};
      if (action.name !== undefined) patch.name = action.name;
      if (action.nameAr !== undefined) patch.nameAr = action.nameAr;
      return updateRecord("expenseCats", action.id, patch);
    }
    if (t === "SET_CAT_BUDGET") {
      return updateRecord("expenseCats", action.id, { monthlyBudget: action.monthlyBudget });
    }

    /* ── Holdings & Balance ────────────────────────────── */
    if (t === "ADD_HOLDING") return addRecord("holdings", action.holding);
    if (t === "UPDATE_HOLDING") return updateRecord("holdings", action.id, action.patch);
    if (t === "REMOVE_HOLDING") return deleteRecord("holdings", action.id);
    if (t === "ADD_HOLDING_DOC") {
      const h = (entities.holdings || []).find((x) => x.id === action.holdingId);
      return updateRecord("holdings", action.holdingId, { docs: [...(h?.docs || []), action.doc] });
    }
    if (t === "REMOVE_HOLDING_DOC") {
      const h = (entities.holdings || []).find((x) => x.id === action.holdingId);
      return updateRecord("holdings", action.holdingId, { docs: (h?.docs || []).filter((d) => d.id !== action.docId) });
    }

    if (t === "ADD_LIABILITY") return addRecord("investmentLiabilities", action.liability);
    if (t === "UPDATE_LIABILITY") return updateRecord("investmentLiabilities", action.id, action.patch);
    if (t === "REMOVE_LIABILITY") return deleteRecord("investmentLiabilities", action.id);
    if (t === "ADD_LIABILITY_DOC") {
      const l = (entities.investmentLiabilities || []).find((x) => x.id === action.liabilityId);
      return updateRecord("investmentLiabilities", action.liabilityId, { docs: [...(l?.docs || []), action.doc] });
    }
    if (t === "REMOVE_LIABILITY_DOC") {
      const l = (entities.investmentLiabilities || []).find((x) => x.id === action.liabilityId);
      return updateRecord("investmentLiabilities", action.liabilityId, { docs: (l?.docs || []).filter((d) => d.id !== action.docId) });
    }

    if (t === "ADD_BALANCE_ITEM") return addRecord("balanceItems", action.item);
    if (t === "REMOVE_BALANCE_ITEM") return deleteRecord("balanceItems", action.id);

    /* ── Projects ──────────────────────────────────────── */
    if (t === "ADD_PROJECT") return addRecord("projects", action.project);
    if (t === "UPDATE_PROJECT") return updateRecord("projects", action.id, { ...action.patch, ...(action.actorId ? { updatedBy: action.actorId } : {}) });
    if (t === "REMOVE_PROJECT") {
      /* Unlink expenses and tasks referencing this project, then delete. */
      const linkedExps = (entities.expenses || []).filter((e) => e.projectId === action.id);
      const expPromises = linkedExps.map((e) => updateRecord("expenses", e.id, { projectId: "", workstreamId: "" }));
      const linkedTasks = (entities.tasks || []).filter((tk) => {
        const cp = tk.contextPayload || tk.context_payload || {};
        const inCtx = cp.projectId === action.id;
        const inLinked = (tk.linkedEntities || tk.linked_entities || []).some((le) => le.type === "project" && le.id === action.id);
        return inCtx || inLinked;
      });
      const taskPromises = linkedTasks.map((tk) => {
        const cp = { ...(tk.contextPayload || tk.context_payload || {}) };
        delete cp.projectId; delete cp.projectName;
        delete cp.workstreamId; delete cp.workstreamName;
        const linked = (tk.linkedEntities || tk.linked_entities || []).filter((le) => !(le.type === "project" && le.id === action.id));
        return updateRecord("tasks", tk.id, { context_payload: cp, linked_entities: linked });
      });
      return Promise.all([...expPromises, ...taskPromises]).then(() => deleteRecord("projects", action.id));
    }
    if (t === "ADD_PROJECT_PERSON") {
      const p = (entities.projects || []).find((x) => x.id === action.projectId);
      return updateRecord("projects", action.projectId, { people: [...(p?.people || []), action.person] });
    }
    if (t === "REMOVE_PROJECT_PERSON") {
      const p = (entities.projects || []).find((x) => x.id === action.projectId);
      return updateRecord("projects", action.projectId, { people: (p?.people || []).filter((pp) => pp.id !== action.personId) });
    }
    if (t === "ADD_PROJECT_DOC") {
      const p = (entities.projects || []).find((x) => x.id === action.projectId);
      return updateRecord("projects", action.projectId, { docs: [...(p?.docs || []), action.doc] });
    }
    if (t === "REMOVE_PROJECT_DOC") {
      const p = (entities.projects || []).find((x) => x.id === action.projectId);
      return updateRecord("projects", action.projectId, { docs: (p?.docs || []).filter((d) => d.id !== action.docId) });
    }
    if (t === "ADD_PROJECT_WORKSTREAM") {
      const p = (entities.projects || []).find((x) => x.id === action.projectId);
      return updateRecord("projects", action.projectId, { workstreams: [...(p?.workstreams || []), action.workstream] });
    }
    if (t === "UPDATE_PROJECT_WORKSTREAM") {
      const p = (entities.projects || []).find((x) => x.id === action.projectId);
      return updateRecord("projects", action.projectId, { workstreams: (p?.workstreams || []).map((w) => w.id === action.workstreamId ? { ...w, ...action.patch } : w) });
    }
    if (t === "REMOVE_PROJECT_WORKSTREAM") {
      const p = (entities.projects || []).find((x) => x.id === action.projectId);
      /* Clear workstreamId on any expenses/tasks that referenced it. */
      const expPromises = (entities.expenses || [])
        .filter((e) => e.projectId === action.projectId && e.workstreamId === action.workstreamId)
        .map((e) => updateRecord("expenses", e.id, { workstreamId: "" }));
      const taskPromises = (entities.tasks || [])
        .filter((tk) => {
          const cp = tk.contextPayload || tk.context_payload || {};
          return cp.projectId === action.projectId && cp.workstreamId === action.workstreamId;
        })
        .map((tk) => {
          const cp = { ...(tk.contextPayload || tk.context_payload || {}) };
          delete cp.workstreamId; delete cp.workstreamName;
          return updateRecord("tasks", tk.id, { context_payload: cp });
        });
      return Promise.all([
        updateRecord("projects", action.projectId, { workstreams: (p?.workstreams || []).filter((w) => w.id !== action.workstreamId) }),
        ...expPromises,
        ...taskPromises,
      ]);
    }

    /* ── Staff ─────────────────────────────────────────── */
    if (t === "ADD_STAFF") return addRecord("staff", action.staff);
    if (t === "UPDATE_STAFF") return updateRecord("staff", action.id, action.patch, { lockCheck: action.lockCheck });
    if (t === "ADD_STAFF_DOC") {
      const s = (entities.staff || []).find((x) => x.id === action.staffId);
      return updateRecord("staff", action.staffId, { docs: [...(s?.docs || []), action.doc] });
    }
    if (t === "REMOVE_STAFF_DOC") {
      const s = (entities.staff || []).find((x) => x.id === action.staffId);
      return updateRecord("staff", action.staffId, { docs: (s?.docs || []).filter((d) => d.id !== action.docId) });
    }
    if (t === "ADD_STAFF_LOAN") {
      const s = (entities.staff || []).find((x) => x.id === action.staffId);
      return updateRecord("staff", action.staffId, { loans: [...(s?.loans || []), action.loan] });
    }
    if (t === "UPDATE_STAFF_LOAN") {
      const s = (entities.staff || []).find((x) => x.id === action.staffId);
      updateRecord("staff", action.staffId, { loans: (s?.loans || []).map((l) => l.id === action.id ? { ...l, ...action.patch } : l) });
      /* Sync back to loan_requests if this loan was created from a request */
      const loan = (s?.loans || []).find((l) => l.id === action.id);
      if (loan?.fromRequest && action.patch.status) {
        const lrStatus = action.patch.status === "settled" ? "settled" : "approved";
        updateRecord("loanRequests", loan.fromRequest, { status: lrStatus });
      }
      return;
    }
    if (t === "ADD_SALARY_HISTORY") {
      const s = (entities.staff || []).find((x) => x.id === action.staffId);
      return updateRecord("staff", action.staffId, { salaryHistory: [...(s?.salaryHistory || []), action.entry] });
    }
    if (t === "UPDATE_LEAVE_BALANCE") {
      return updateRecord("staff", action.staffId, { leaveBalance: action.balance });
    }
    if (t === "SET_ANNUAL_LEAVE_DAYS") {
      return updateRecord("staff", action.staffId, { annualLeaveDays: action.days });
    }
    if (t === "ADD_PAYSLIP") {
      const s = (entities.staff || []).find((x) => x.id === action.staffId);
      return updateRecord("staff", action.staffId, { payslips: [...(s?.payslips || []), action.payslip] });
    }
    if (t === "UPDATE_PAYSLIP") {
      const s = (entities.staff || []).find((x) => x.id === action.staffId);
      return updateRecord("staff", action.staffId, { payslips: (s?.payslips || []).map((ps) => ps.id === action.id ? { ...ps, ...action.patch } : ps) });
    }

    /* ── Leave & Loan Requests ─────────────────────────── */
    if (t === "ADD_LEAVE_REQ") return addRecord("leaveRequests", action.req);
    if (t === "UPDATE_LEAVE_REQ") return updateRecord("leaveRequests", action.id, action.patch);
    if (t === "ADD_LOAN_REQUEST") return addRecord("loanRequests", action.req);
    if (t === "UPDATE_LOAN_REQUEST") return updateRecord("loanRequests", action.id, action.patch);
    /* Generic config setter — persists any whitelisted CONFIG_KEYS value.
       Used by modules (e.g. Smart Home) that don't need a bespoke
       action type per config key. Silently ignores non-whitelisted
       keys to prevent accidental persistence of arbitrary state. */
    if (t === "SET_CONFIG_KEY") {
      const k = action.payload?.key;
      if (!k || !CONFIG_KEYS.includes(k)) return;
      return setConfigKey(k, action.payload.value);
    }
    if (t === "SET_STAFF_AUTHORITIES") return setConfigKey("staffAuthorities", { ...(config.staffAuthorities || {}), ...action.patch });
    if (t === "ADD_STAFF_ROLE") return setConfigKey("staffRoles", [...(config.staffRoles || []), action.role]);
    if (t === "REMOVE_STAFF_ROLE") return setConfigKey("staffRoles", (config.staffRoles || []).filter((r) => r !== action.role));
    if (t === "SET_STAFF_PERMISSIONS") return setConfigKey("staffPermissions", { ...(config.staffPermissions || {}), ...action.patch });
    if (t === "SET_STAFF_VIEW_PERMS")  return setConfigKey("staffViewPermissions", { ...(config.staffViewPermissions || {}), ...action.patch });
    if (t === "SET_FACILITY_PERMS") return setConfigKey("facilityPermissions", { ...(config.facilityPermissions || {}), ...action.patch });
    if (t === "SET_FACILITY_CONFIG") return setConfigKey("facilityConfig", { ...(config.facilityConfig || {}), ...action.patch });
    if (t === "SET_INCOME_PERMS") return setConfigKey("incomePermissions", { ...(config.incomePermissions || {}), ...action.patch });
    if (t === "SET_INCOME_ALERT_SETTINGS") return setConfigKey("incomeAlertSettings", { ...(config.incomeAlertSettings || {}), ...action.patch });
    if (t === "SET_EXPENSE_PERMS") return setConfigKey("expensePermissions", { ...(config.expensePermissions || {}), ...action.patch });
    if (t === "SET_EXPENSE_ALERT_SETTINGS") return setConfigKey("expenseAlertSettings", { ...(config.expenseAlertSettings || {}), ...action.patch });
    if (t === "SET_INVESTMENT_PERMS") return setConfigKey("investmentPermissions", { ...(config.investmentPermissions || {}), ...action.patch });
    if (t === "SET_INVEST_ALERT_SETTINGS") return setConfigKey("investmentAlertSettings", { ...(config.investmentAlertSettings || {}), ...action.patch });
    if (t === "SET_FINANCIAL_TARGETS") return setConfigKey("financialTargets", { ...(config.financialTargets || {}), ...action.patch });

    /* ── Users ─────────────────────────────────────────── */
    if (t === "ADD_USER") return addRecord("users", action.user);
    if (t === "DELETE_USER") return deleteRecord("users", action.id);
    if (t === "RENAME_USER") return updateRecord("users", action.id, { name: action.name, initials: action.name.slice(0, 2).toUpperCase() });
    if (t === "TOGGLE_USER") {
      const u = (entities.users || []).find((x) => x.id === action.id);
      return updateRecord("users", action.id, { active: !u?.active });
    }
    if (t === "SET_USER_PASSWORD") return updateRecord("users", action.id, { password: action.password, email: action.email, phone: action.phone });
    if (t === "UPDATE_USER_PROFILE") return updateRecord("users", action.id, { email: action.email, phone: action.phone, preferred_lang: action.preferredLang || "en" });
    if (t === "SET_USER_PERMS") return updateRecord("users", action.id, { allowedCats: action.allowedCats, allowedLocs: action.allowedLocs });

    /* ── Locations ─────────────────────────────────────── */
    if (t === "ADD_LOC") return addRecord("locations", action.loc);
    if (t === "UPDATE_LOC") return updateRecord("locations", action.id, action.patch);
    if (t === "REMOVE_LOC") return deleteRecord("locations", action.id);

    /* ── Vehicles ──────────────────────────────────────── */
    if (t === "ADD_VEHICLE") return addRecord("vehicles", action.v);
    if (t === "UPDATE_VEHICLE") return updateRecord("vehicles", action.id, action.patch);
    if (t === "REMOVE_VEHICLE") {
      deleteRecord("vehicles", action.id);
      /* Orphan cleanup: null out vehicle_id on linked expense_records and transport_requests */
      const linked = (entities.expenses || []).filter((e) => e.vehicleId === action.id);
      for (const e of linked) updateRecord("expenses", e.id, { vehicle_id: null });
      const linkedTrips = (entities.transportRequests || []).filter((r) => r.vehicleId === action.id);
      for (const r of linkedTrips) updateRecord("transportRequests", r.id, { vehicle_id: null });
      /* Cascade-delete denormalized maintenance and mileage records */
      const linkedMaint = (entities.vehicleMaintRecords || []).filter((m) => m.vehicleId === action.id);
      for (const m of linkedMaint) deleteRecord("vehicleMaintRecords", m.id);
      const linkedMileage = (entities.vehicleMileageLog || []).filter((m) => m.vehicleId === action.id);
      for (const m of linkedMileage) deleteRecord("vehicleMileageLog", m.id);
      return;
    }
    if (t === "SET_FLEET_CONFIG") return setConfigKey("fleetConfig", { ...(config.fleetConfig || {}), ...action.patch });
    if (t === "SET_FLEET_PERMS") return setConfigKey("fleetPermissions", { ...(config.fleetPermissions || {}), ...action.patch });

    /* Vehicle maintenance records (Phase 5: denormalized from vehicles.maint_records JSON) */
    if (t === "ADD_VEH_MAINT") return addRecord("vehicleMaintRecords", action.record);
    if (t === "UPDATE_VEH_MAINT") return updateRecord("vehicleMaintRecords", action.id, action.patch);
    if (t === "REMOVE_VEH_MAINT") return deleteRecord("vehicleMaintRecords", action.id);
    /* Vehicle mileage log (append-only; denormalized from vehicles.mileage_log JSON) */
    if (t === "ADD_VEH_MILEAGE") return addRecord("vehicleMileageLog", action.entry);
    if (t === "REMOVE_VEH_MILEAGE") return deleteRecord("vehicleMileageLog", action.id);

    /* ── Groceries ─────────────────────────────────────── */
    if (t === "ADD_GROCERY") return addRecord("groceries", action.item);
    if (t === "UPDATE_GROCERY") return updateRecord("groceries", action.id, action.patch);
    if (t === "SET_GROCERY_CONFIG") return setConfigKey("groceryConfig", { ...(config.groceryConfig || {}), ...action.patch });
    if (t === "ADD_GROCERY_BATCH") {
      for (const item of action.items) addRecord("groceries", item);
      if (action.notif) addRecord("notifications", action.notif);
      return;
    }

    /* ── Maintenance & Suppliers ────────────────────────── */
    if (t === "ADD_MAINT") return addRecord("maintenance", action.item);
    if (t === "UPDATE_MAINT") return updateRecord("maintenance", action.id, action.patch);
    if (t === "REMOVE_MAINT") return deleteRecord("maintenance", action.id);
    if (t === "SET_MAINT_STATUS") return updateRecord("maintenance", action.id, { status: action.status });
    if (t === "ADD_MAINT_SCHEDULE") return addRecord("maintenanceSchedules", action.item);
    if (t === "UPDATE_MAINT_SCHEDULE") return updateRecord("maintenanceSchedules", action.id, action.patch);
    if (t === "REMOVE_MAINT_SCHEDULE") return deleteRecord("maintenanceSchedules", action.id);
    if (t === "ADD_SUPPLIER") return addRecord("suppliers", action.supplier);
    if (t === "UPDATE_SUPPLIER") return updateRecord("suppliers", action.id, action.patch);
    if (t === "REMOVE_SUPPLIER") {
      deleteRecord("suppliers", action.id);
      /* Orphan cleanup: null out supplier_id on linked maintenance records (expenses retain supplier for historical spend tracking) */
      const linked = (entities.maintenance || []).filter((m) => m.supplierId === action.id);
      for (const m of linked) updateRecord("maintenance", m.id, { supplier_id: null });
      return;
    }
    if (t === "ADD_MAINT_CAT") return setConfigKey("maintenanceCategories", [...(config.maintenanceCategories || []), action.cat]);
    if (t === "REMOVE_MAINT_CAT") return setConfigKey("maintenanceCategories", (config.maintenanceCategories || []).filter((c) => c !== action.cat));
    if (t === "ADD_GROCERY_CAT") return setConfigKey("groceryCategories", [...(config.groceryCategories || []), action.cat]);
    if (t === "REMOVE_GROCERY_CAT") return setConfigKey("groceryCategories", (config.groceryCategories || []).filter((c) => c !== action.cat));
    if (t === "ADD_FLEET_CAT") return setConfigKey("fleetServiceCategories", [...(config.fleetServiceCategories || []), action.cat]);
    if (t === "REMOVE_FLEET_CAT") return setConfigKey("fleetServiceCategories", (config.fleetServiceCategories || []).filter((c) => c !== action.cat));
    if (t === "SET_SUPPLIER_PERMS") return setConfigKey("supplierPermissions", { ...(config.supplierPermissions || {}), ...action.patch });
    if (t === "SET_PROJECT_PERMS") return setConfigKey("projectPermissions", { ...(config.projectPermissions || {}), ...action.patch });
    if (t === "SET_PROJECT_CONFIG") return setConfigKey("projectConfig", { ...(config.projectConfig || {}), ...action.patch });
    if (t === "SET_SUPPLIER_CONFIG") return setConfigKey("supplierConfig", { ...(config.supplierConfig || {}), ...action.patch });

    /* Tasks flow exclusively through the Task Engine (ADD_TASK /
       UPDATE_TASK); legacy driverTasks/maidTasks sync removed. */

    /* ── Transport ─────────────────────────────────────── */
    if (t === "ADD_TRANSPORT") return addRecord("transportRequests", action.req);
    if (t === "UPDATE_TRANSPORT") return updateRecord("transportRequests", action.id, action.patch);
    /* Local-only patch: update entities without API PUT (safe for linking taskId before POST completes) */
    if (t === "PATCH_TRANSPORT_LOCAL") return setEntities((prev) => ({
      ...prev,
      transportRequests: (prev.transportRequests || []).map((r) => r.id === action.id ? { ...r, ...action.patch } : r),
    }));
    if (t === "SET_TRANSPORT_CONFIG") return setConfigKey("transportConfig", { ...(config.transportConfig || {}), ...action.patch });
    if (t === "SET_TRANSPORT_PERMS") return setConfigKey("transportPermissions", { ...(config.transportPermissions || {}), ...action.patch });

    /* ── Task Engine (tasks are persisted via /api/tasks, not /api/crud) ── */
    if (t === "ADD_TASK") return setEntities((prev) => ({ ...prev, tasks: [action.task, ...(prev.tasks || [])] }));
    if (t === "UPDATE_TASK") return setEntities((prev) => ({ ...prev, tasks: (prev.tasks || []).map(tk => tk.id === action.id ? { ...tk, ...action.patch } : tk) }));
    if (t === "REMOVE_TASK") return setEntities((prev) => ({ ...prev, tasks: (prev.tasks || []).filter(tk => tk.id !== action.id) }));
    if (t === "ADD_TASK_CONFIG") return addRecord("taskConfigs", action.config);
    if (t === "UPDATE_TASK_CONFIG") return updateRecord("taskConfigs", action.id, action.patch);
    if (t === "REMOVE_TASK_CONFIG") return deleteRecord("taskConfigs", action.id);
    if (t === "ADD_TASK_ACTIVITY") return addRecord("taskActivityLog", action.log);
    if (t === "ADD_TASK_ESCALATION") return addRecord("taskEscalationLog", action.log);
    /* Task Taxonomy (workspace scope) — writes via /api/tasks actions in Batch 1.3 */
    if (t === "ADD_TASK_TAXONOMY_ENTRY") return addRecord("taskTaxonomy", action.entry);
    if (t === "UPDATE_TASK_TAXONOMY_ENTRY") return updateRecord("taskTaxonomy", action.id, action.patch);
    if (t === "REMOVE_TASK_TAXONOMY_ENTRY") return deleteRecord("taskTaxonomy", action.id);

    /* ── Calendar ──────────────────────────────────────── */
    if (t === "ADD_CAL_ACCOUNT") return addRecord("calAccounts", action.acc);
    if (t === "REMOVE_CAL_ACCOUNT") {
      deleteRecord("calAccounts", action.id);
      const toRemove = (entities.meetings || []).filter((m) => m.accountId === action.id);
      for (const m of toRemove) deleteRecord("meetings", m.id);
      return;
    }
    if (t === "TOGGLE_CAL") {
      const c = (entities.calAccounts || []).find((x) => x.id === action.id);
      return updateRecord("calAccounts", action.id, { active: !c?.active });
    }
    if (t === "ADD_MEETING") return addRecord("meetings", action.meeting);
    if (t === "REMOVE_MEETING") return deleteRecord("meetings", action.id);

    /* ── Meals & Menu ──────────────────────────────────── */
    if (t === "ADD_MEAL") return addRecord("meals", action.meal);
    if (t === "UPDATE_MEAL") return updateRecord("meals", action.id, action.patch);
    if (t === "REMOVE_MEAL") return deleteRecord("meals", action.id);
    if (t === "SET_MEAL_PREF") {
      const existing = (entities.mealPreferences || []).find((p) => p.userId === action.userId && p.mealId === action.mealId);
      const newPref = { id: existing?.id || `mp_${Date.now()}`, userId: action.userId, mealId: action.mealId, score: action.score };
      if (existing) return updateRecord("mealPreferences", existing.id, { score: action.score });
      return addRecord("mealPreferences", newPref);
    }
    if (t === "ADD_MENU_CYCLE") return addRecord("menuCycles", action.cycle);
    if (t === "UPDATE_MENU_CYCLE") return updateRecord("menuCycles", action.id, action.patch);
    if (t === "ADD_MEAL_CAT") return setConfigKey("mealCategories", [...(config.mealCategories || []), action.cat]);
    if (t === "REMOVE_MEAL_CAT") return setConfigKey("mealCategories", (config.mealCategories || []).filter((c) => c !== action.cat));
    if (t === "ADD_GENERATED_OPTIONS") return addRecord("menuGeneratedOptions", action.options);
    if (t === "ADD_MENU_GENERATED_OPTION") return addRecord("menuGeneratedOptions", action.option);
    if (t === "UPDATE_MENU_GENERATED_OPTION") return updateRecord("menuGeneratedOptions", action.id, action.patch);
    if (t === "SET_GENERATED_OPTIONS_BATCH") {
      setEntities((prev) => {
        const existing = (prev.menuGeneratedOptions || []).filter((o) => o.cycleId !== action.cycleId);
        return { ...prev, menuGeneratedOptions: [...action.options, ...existing] };
      });
      return;
    }
    if (t === "CAST_MENU_VOTE") {
      const existing = (entities.menuVotes || []).find(
        (v) => v.cycleId === action.vote.cycleId && v.day === action.vote.day &&
               v.mealType === action.vote.mealType && v.userId === action.vote.userId &&
               v.dishOptionId === action.vote.dishOptionId
      );
      if (existing) return updateRecord("menuVotes", existing.id, { dishName: action.vote.dishName });
      return addRecord("menuVotes", action.vote);
    }
    if (t === "REMOVE_MENU_VOTE") return deleteRecord("menuVotes", action.id);
    if (t === "SET_MENU_DIETARY_REQS") return setConfigKey("menuDietaryRequirements", action.requirements);
    if (t === "SET_MENU_PERMISSIONS") return setConfigKey("menuPermissions", { ...(config.menuPermissions || {}), ...action.patch });
    if (t === "SET_MENU_PREFERENCES") return setConfigKey("menuPreferences", action.preferences);
    if (t === "SET_MENU_DEADLINE") return setConfigKey("menuDeadlineOverride", action.deadline);
    if (t === "SET_MENU_VOTING_MODE") return setConfigKey("menuVotingModeOverride", action.mode);
    if (t === "SET_MENU_OPTIONS_PER_COURSE") return setConfigKey("menuOptionsPerCourseOverride", action.value);
    if (t === "SET_MENU_COURSES_OVERRIDE") return setConfigKey("menuCoursesOverride", action.value);
    if (t === "SET_HOUSEHOLD_DATA") return setConfigKey("householdData", action.data);

    /* ── Notifications ─────────────────────────────────── */
    if (t === "ADD_NOTIF") return addRecord("notifications", action.notif);
    if (t === "MARK_NOTIF_READ") return updateRecord("notifications", action.id, { read: true });
    if (t === "MARK_ALL_READ") {
      const toMark = (entities.notifications || []).filter((n) => n.userId === action.userId && !n.read);
      for (const n of toMark) updateRecord("notifications", n.id, { read: true });
      return;
    }

    /* ── Config (scalar settings) ──────────────────────── */
    if (t === "SET_LANG") return setConfigKey("lang", action.lang);
    if (t === "SET_CURRENCY") return setConfigKey("currency", action.currency);
    if (t === "SET_APP_NAME") return setConfigKey("appName", action.name);
    if (t === "SET_TAGLINE") return setConfigKey("tagline", action.tagline);
    if (t === "SET_LOC") return setConfigKey("locFilter", action.id);
    if (t === "SET_MODULE") return setConfigKey("modules", { ...(config.modules || {}), [action.key]: action.on });
    if (t === "SET_NAV_ORDER") return setConfigKey("navOrder", action.order);
    if (t === "SET_MODULE_SUB") return setConfigKey("moduleSubtitles", { ...(config.moduleSubtitles || {}), [action.key]: action.sub });
    if (t === "SET_API_KEY") return setConfigKey("apiKeys", { ...(config.apiKeys || {}), [action.key]: action.value });
    if (t === "SET_BRANDING") return setConfigKey("branding", { ...(config.branding || {}), ...action.patch });
    if (t === "SET_NOTIF_PREF") return setConfigKey("notifPrefs", { ...(config.notifPrefs || {}), [action.key]: { ...((config.notifPrefs || {})[action.key] || {}), ...action.patch } });
    if (t === "SET_DASHBOARD_CONFIG") return setConfigKey("dashboardConfig", action.config);
    if (t === "ADD_DOC_TAG") return setConfigKey("docTags", [...(config.docTags || []), action.tag]);
    if (t === "REMOVE_DOC_TAG") return setConfigKey("docTags", (config.docTags || []).filter((x) => x !== action.tag));
    if (t === "ADD_INV_CAT") return setConfigKey("investmentCategories", [...(config.investmentCategories || []), action.cat]);
    if (t === "REMOVE_INV_CAT") return setConfigKey("investmentCategories", (config.investmentCategories || []).filter((c) => (typeof c==="string"?c:c.name) !== action.name));
    if (t === "UPDATE_INV_CAT") return setConfigKey("investmentCategories", (config.investmentCategories || []).map((c) => { const n=typeof c==="string"?{name:c,listed:false}:c; return n.name===action.name?{...n,...action.patch}:n; }));
    if (t === "ADD_HAT") {
      if ((config.hats || []).includes(action.hat)) return;
      return setConfigKey("hats", [...(config.hats || []), action.hat]);
    }
    if (t === "REMOVE_HAT") return setConfigKey("hats", (config.hats || []).filter((h) => h !== action.hat));

    /* ── Task Visibility Overrides ────────────────────── */
    if (t === "SET_TASK_VISIBILITY_OVERRIDE") return setConfigKey("taskVisibilityOverrides", { ...(config.taskVisibilityOverrides || {}), [action.role]: { ...((config.taskVisibilityOverrides || {})[action.role] || {}), ...action.patch } });
    if (t === "CLEAR_TASK_VISIBILITY_OVERRIDE") { const o = { ...(config.taskVisibilityOverrides || {}) }; delete o[action.role]; return setConfigKey("taskVisibilityOverrides", o); }

    /* ── AI workspace controls ────────────────────────── */
    if (t === "SET_WS_AI_ALL_DISABLED") return setConfigKey("wsAiAllDisabled", action.disabled);
    if (t === "SET_WS_AI_DISABLED") return setConfigKey("wsAiDisabled", { ...(config.wsAiDisabled || {}), [action.moduleKey]: action.disabled });

    console.warn("[combinedDispatch] unhandled action:", t);
  }, [entities, config, wsKey, addRecord, updateRecord, deleteRecord, setConfigKey]);

  /* ── 6. Force-update: direct API + state mutation for critical actions ── */
  /* Bypasses combinedDispatch / optimistic / rollback entirely.
     Calls the API first, then applies the confirmed result to local state. */
  const forceUpdate = useCallback(async (stateKey, id, patch) => {
    const table = STATE_TO_TABLE[stateKey];
    if (!table) throw new Error("Unknown stateKey: " + stateKey);

    /* Lock: prevent refetchAll and realtime from touching this ID */
    if (!optimisticRef.current[stateKey]) optimisticRef.current[stateKey] = new Set();
    optimisticRef.current[stateKey].add(id);
    pendingWritesRef.current += 1;

    try {
      /* Call API — no retries, fail fast */
      const r = await authFetch(`/api/crud?table=${table}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ table, ws_key: wsKey, id, patch }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: r.statusText }));
        throw new Error(err.error || r.statusText);
      }
      const result = await r.json();
      const confirmedRow = result.row;

      /* Apply the DB-confirmed row directly to state.
         If server returned a row, merge it. Otherwise just apply our patch. */
      setEntities((prev) => ({
        ...prev,
        [stateKey]: (prev[stateKey] || []).map((r) =>
          r.id === id ? { ...r, ...(confirmedRow || patch), updatedAt: new Date().toISOString() } : r
        ),
      }));

      /* Release locks after a safe window */
      setTimeout(() => { pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1); }, 10000);
      setTimeout(() => { optimisticRef.current[stateKey]?.delete(id); }, 30000);

      return confirmedRow;
    } catch (err) {
      optimisticRef.current[stateKey]?.delete(id);
      pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
      throw err;
    }
  }, [wsKey]);

  /* ── 7. Composed state (backward-compatible shape) ────── */
  const state = useMemo(() => ({
    ...config,
    ...entities,
  }), [entities, config]);

  return { state, dispatch: combinedDispatch, forceUpdate, loading, syncError };
}
